import * as React from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import HelpAdornment from './HelpAdornment';
import { createFilterOptions } from '@mui/material/Autocomplete';

const UNCATEGORIZED = 'Uncategorized';
const EMPTY_TAGS = Object.freeze([]); // stable fallback (no new [] each render)
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

// Filter keeps original sort order; searches label and folder label (accent-insensitive)
const filterOptions = createFilterOptions({
  ignoreAccents: true,
  stringify: (option) =>
    `${option.label} ${(option.folder?.label || UNCATEGORIZED)}`,
});

export default function TagFilterSelect({
  label = "Tags",
  error = false,
  required = false,
  fullWidth = false,
  size = "small",
  onChange,
  value = [],
  placeholder = "Select tags...",
  hideHelpAdornment = false,
  helpKey
}) {
  // Raw list from Redux (may be undefined/null before load)
  const tagsRaw = useSelector(state => state.tag.list);
  const baseTags = tagsRaw ?? EMPTY_TAGS;

  // Sort by folder label (asc), then tag label (asc), then id (asc) for stability
  const tags = React.useMemo(() => {
    const copy = baseTags.slice();
    copy.sort((a, b) => {
      const fa = a.folder?.label || UNCATEGORIZED;
      const fb = b.folder?.label || UNCATEGORIZED;
      const byFolder = collator.compare(fa, fb);
      if (byFolder !== 0) return byFolder;

      const byLabel = collator.compare(a.label, b.label);
      if (byLabel !== 0) return byLabel;

      return (a.id ?? 0) - (b.id ?? 0);
    });
    return copy;
  }, [baseTags]);

  // Map selected ids -> option objects (and ignore any not found)
  const selectedTags = React.useMemo(
    () =>
      (value || [])
        .map(valId => tags.find(tag => tag.id === (valId?.id ?? valId)))
        .filter(Boolean),
    [value, tags]
  );

  return (
    <Autocomplete
      multiple
      options={tags}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => option.folder?.label || UNCATEGORIZED}
      value={selectedTags}
      onChange={(e, newValue) => onChange(newValue.map(tag => tag.id))}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      size={size}
      disableCloseOnSelect
      filterOptions={filterOptions}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={option.id}
            label={option.label}
            {...getTagProps({ index })}
            size={size}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: hideHelpAdornment ? null : (
              <HelpAdornment helpKey={helpKey || "tags"} />
            ),
          }}
        />
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ pl: 1, pt: 0.5 }}>
            {params.group}
          </Typography>
          <ul style={{ margin: 0, padding: 0 }}>
            {params.children}
          </ul>
        </li>
      )}
      fullWidth={fullWidth}
    />
  );
}
